// modernTools — Modern Layout の「ツール / モディファイア分離」(§9.3) を表す純粋モデル.
//
// 方針 (docs/_wip/modern-layout.md §9.6 / §9.3、consult 2026-06-08):
//   - Modern UI は Classic 部品を流用せず engine 層だけ共有して全面新規 (§9.6)。
//   - ツール帯に出すのは **相互排他ツール** のみ: ブラシ / 消しゴム / 手のひら(pan) /
//     ズーム。図形=後続スライス、スポイト=ジェスチャ (右クリック/Alt+click) なので
//     ボタンを出さない (§9.6 新 UI の具体)。
//   - **ペン種 (= ブラシのサブツール)** と **消しゴム** は engine の `StrokePen`
//     (`'brush' | 'flat' | 'eraser'`) に直結する。手のひら/ズームは描画ツールでない
//     ナビなのでここでは「アクティブツール」の概念に含めず、各レイアウトが pan/zoom
//     を別に扱う (シェルの zoom は engine.setZoom、pan は canvas overflow scroll)。
//   - **UI 表示名**: Modern は「ブラシ」(内部 `'brush'`)。Classic の「水彩」とは別の
//     文字列を Modern 専用に持つ (CLAUDE.md「ペン UI 表示名」)。サインペン=`'flat'`。
//
// このファイルは DOM/React に触れない pure module。vitest (node env) で
// テスト可能にする (= colorWheel.ts / maskHelpers.ts と同流儀)。

import type { StrokePen } from '@oekaki/protocol';

/**
 * Modern ツール帯の相互排他ツール ID。
 * - `brush` / `flat` / `eraser` は engine の `StrokePen` にそのまま対応する描画ツール。
 * - `spuit` (スポイト) は描画でない色拾いツール。**持続モード** (選び直すまで継続、
 *   素クリックで色拾い、ストローク非開始)。色拾い自体は protocol 非依存・client
 *   ローカル (`spuit.ts` の `pickColorFromEngine`、§9.6 方針転換 2026-06-12)。
 * - `pan` (手のひら) / `zoom` は描画でないナビツール。
 *
 * ※ シェル時点では pan/zoom はボタンを「ナビのヒント」として置くだけで、実際の
 *   pan は canvas の overflow scroll、zoom は engine.setZoom が担う (描画能力非追加)。
 */
export type ModernTool = StrokePen | 'spuit' | 'pan' | 'zoom';

/** ブラシ bucket のサブツール (= ペン種)。§9.3「ブラシ / サインペン」。 */
export const MODERN_BRUSH_SUBTOOLS: ReadonlyArray<{
  readonly pen: Extract<
    StrokePen,
    'brush' | 'flat' | 'texture' | 'tone' | 'airbrush' | 'crayon' | 'pastel' | 'dot'
  >;
  readonly label: string;
}> = [
  { pen: 'brush', label: 'ブラシ' },
  { pen: 'flat', label: 'サインペン' },
  // ドット (phase11-light-pens-2026-08-21.md §3/§6): Phase 11 軽量ペン、露出済み。
  // Wasm snapshot 経路のパリティ破れは解消済み (parity test が ON/OFF 両方で緑)。
  // 表現パリティ原則により Classic / Modern 同時投入。desktop ブラシパネルの指定席は
  // 「線画」タブ (`ModernBrushPanel.tsx` の `findSubtool` 参照)。
  { pen: 'dot', label: 'ドット' },
  // texture spike (texture-brush-design.md §8): Modern 限定の暫定ボタン。
  // preset selector (紙/毛先/雲/カケアミ) は §10.1 の 2階層 UI で出す。
  // Classic は懐古純度維持で対象外。
  { pen: 'texture', label: 'テクスチャ' },
  // クレヨン / パステル (phase11-light-pens-2026-08-21.md §2/§6): Phase 11 軽量ペン。
  // 表現パリティ原則により Classic / Modern 同時投入 (airbrush と同じ扱い)。desktop
  // ブラシパネルの指定席は「質感」タブ (`ModernBrushPanel.tsx` の `findSubtool` 参照)。
  { pen: 'crayon', label: 'クレヨン' },
  { pen: 'pastel', label: 'パステル' },
  // エアブラシ (airbrush-design-2026-08-15.md §0 / §6): β 目玉ペン 2 本目、texture と
  // 対。表現パリティ原則 (§0 確定事項) によりリリース当初から Classic / Modern
  // 両方に出す (texture の「いったん Modern 限定」始まりとは異なる)。
  { pen: 'airbrush', label: 'エアブラシ' },
  // スクリーントーン (texture-brush-design.md §12.2): 独立カテゴリ `pen='tone'`。
  // **封印解除 (phase11-light-pens-2026-08-21.md §4)**: 2026-06-20 に「思った感じに
  // ならない」(= 陰影トーンとして見ていた) ため一旦保留していたが、期待されていたのは
  // 「網点の番手」ではなく「装飾のドット柄」だったと判明 (§4.2)。pitch (間隔) を
  // パラメータ化 (8/16/32/64) して封印解除する。desktop ブラシパネルの指定席は
  // 「線画」タブ (dot と同じ、`ModernBrushPanel.tsx` の `findSubtool` 参照)。
  { pen: 'tone', label: 'トーン' },
];

/** Modern UI のペン種表示名 (Classic「水彩」と分離、§9.6 / CLAUDE.md)。 */
export const MODERN_PEN_LABEL: Readonly<Record<StrokePen, string>> = {
  brush: 'ブラシ',
  flat: 'サインペン',
  // `'marker'` は撤去進行中 (UI 非表示)。残置 enum 互換のためラベルだけ定義。
  marker: 'マーカー',
  eraser: '消しゴム',
  // texture spike (texture-brush-design.md §8): Modern のみに出す暫定ペン。
  // 「紙」(world mapping) 固定。preset 名は横展開フェーズで確定。
  texture: 'テクスチャ',
  // スクリーントーン (texture-brush-design.md §12.2): Modern の独立カテゴリ。
  // 番手スライダ UI は frontend で配線する。
  tone: 'トーン',
  // エアブラシ (airbrush-design-2026-08-15.md §0 / §6): Classic / Modern 共通表示名
  // (内部 pen='airbrush' で統一、両レイアウトとも「エアブラシ」— brush の
  // 水彩/ブラシのような呼び分けは無い)。
  airbrush: 'エアブラシ',
  // クレヨン / パステル (phase11-light-pens-2026-08-21.md §6): 質感タブの chip として
  // 実装済 (`MODERN_BRUSH_SUBTOOLS` 参照)。
  crayon: 'クレヨン',
  pastel: 'パステル',
  // ドット (同 §3 / §6): 露出済み (`MODERN_BRUSH_SUBTOOLS` 参照)。旧 Wasm snapshot 経路の
  // パリティ破れは解消済み (parity test が ON/OFF 両方で緑)。
  dot: 'ドット',
};

/**
 * 現在の `StrokePen` から「ツール帯でハイライトすべきツール」を導く。
 * - eraser → 消しゴムツール
 * - brush/flat/marker → ブラシツール (サブツールはプロパティ側で選ぶ)
 */
export const activeToolForPen = (pen: StrokePen): 'brush' | 'eraser' =>
  pen === 'eraser' ? 'eraser' : 'brush';

/**
 * ツール帯でハイライトすべきツールの最終導出 (= スポイト持続モードを優先)。
 *
 * スポイトは pen state とは独立した持続モード (§9.6 方針転換)。スポイトモード中は
 * 描画色 (pen) が何であってもツール帯では **スポイトだけ** を点灯させ、ブラシ/消しゴム
 * は点灯させない (= ユーザが「今スポイト中」を一目で分かる)。スポイトを解除すると
 * pen 由来 (`activeToolForPen`) のハイライトに戻る。
 *
 * @param pen 現在の描画ペン種。
 * @param spuitActive スポイト持続モードが ON か。
 */
export const highlightToolFor = (
  pen: StrokePen,
  spuitActive: boolean,
  rotateToolActive = false,
): 'brush' | 'eraser' | 'spuit' | 'rotate' =>
  rotateToolActive ? 'rotate' : spuitActive ? 'spuit' : activeToolForPen(pen);

/**
 * 回転角度表示 (「23°」) の文言。整数に丸め、-0 は 0 に正規化する。0° (rotation === 0)
 * のときは null = 出さない。丸めて 0 になる微小角 (0.3° 等) は「0°」を出す (= 押して
 * 厳密に 0 へ戻せる)。
 */
export const formatRotationLabel = (rotation: number): string | null => {
  if (rotation === 0) return null;
  const n = Math.round(rotation);
  return `${n === 0 ? 0 : n}°`;
};
